import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Typography,
  Switch,
  InputNumber,
  Upload,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { documentsAPI, getImageUrl } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Documents = () => {
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedName, setUploadedName] = useState('');
  const [form] = Form.useForm();
  const fileUrl = Form.useWatch('fileUrl', form);

  useEffect(() => {
    fetchDocuments();
  }, [pagination.current, pagination.pageSize]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await documentsAPI.getAll({
        page: pagination.current,
        limit: pagination.pageSize
      });
      if (response.data.success) {
        setDocuments(response.data.data.documents);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      }
    } catch (error) {
      message.error('获取文档列表失败');
      console.error('Fetch documents error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingDoc(null);
    setUploadedName('');
    form.resetFields();
    form.setFieldsValue({ isPublished: true, isVip: false, icon: '📄', pages: 1 });
    setModalVisible(true);
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setUploadedName(doc.fileUrl ? doc.fileUrl.split('/').pop() : '');
    form.setFieldsValue(doc);
    setModalVisible(true);
  };

  const handleFileUpload = async (file) => {
    const raw = file?.originFileObj || file;
    if (!raw) return false;
    setUploadingFile(true);
    try {
      const res = await documentsAPI.uploadFile(raw);
      if (!res.data?.success) throw new Error(res.data?.message || '上传失败');
      const url = res.data.data.url;
      form.setFieldsValue({ fileUrl: url });
      setUploadedName(res.data.data.filename || raw.name);
      message.success('文件上传成功');
    } catch (error) {
      message.error(error.response?.data?.message || error.message || '文件上传失败');
    } finally {
      setUploadingFile(false);
    }
    return false;
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      const submitData = {
        ...values,
        fileUrl: values.fileUrl || editingDoc?.fileUrl || '',
      };

      if (!submitData.fileUrl) {
        message.error('请先上传文档文件');
        return;
      }

      if (editingDoc) {
        await documentsAPI.update(editingDoc.id, submitData);
        message.success('文档更新成功');
      } else {
        await documentsAPI.create(submitData);
        message.success('文档创建成功');
      }
      
      setModalVisible(false);
      fetchDocuments();
    } catch (error) {
      message.error('操作失败');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await documentsAPI.delete(id);
      message.success('文档已删除');
      fetchDocuments();
    } catch (error) {
      message.error('删除失败');
      console.error('Delete error:', error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const columns = [
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 60,
      render: (icon) => <span style={{ fontSize: 24 }}>{icon || '📄'}</span>,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => {
        const colors = { strategies: 'green', analysis: 'blue', guides: 'gold', references: 'purple' };
        const labels = { strategies: '交易策略', analysis: '市场分析', guides: '新手指南', references: '快速参考' };
        return <Tag color={colors[cat]}>{labels[cat] || cat}</Tag>;
      },
    },
    {
      title: '页数',
      dataIndex: 'pages',
      key: 'pages',
    },
    {
      title: '下载量',
      dataIndex: 'downloads',
      key: 'downloads',
      render: (downloads) => downloads?.toLocaleString() || 0,
    },
    {
      title: 'VIP',
      dataIndex: 'isVip',
      key: 'isVip',
      render: (isVip) => isVip ? <Tag color="gold">VIP</Tag> : <Tag>免费</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'isPublished',
      key: 'isPublished',
      render: (isPublished) => <Tag color={isPublished ? 'green' : 'default'}>{isPublished ? '已发布' : '草稿'}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>文档管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加文档
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={documents}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          locale={{ emptyText: '暂无文档数据' }}
        />
      </Card>

      <Modal
        title={editingDoc ? '编辑文档' : '添加文档'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述" rules={[{ required: true, message: '请输入描述' }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name="fileUrl" hidden rules={[{ required: true, message: '请上传文档文件' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="文档文件" required>
            <Upload.Dragger
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
              showUploadList={false}
              beforeUpload={handleFileUpload}
              disabled={uploadingFile}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined style={{ fontSize: 36, color: '#2563eb' }} />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此处上传</p>
              <p className="ant-upload-hint">支持 PDF、Word、Excel、PPT、TXT、ZIP，最大 50MB</p>
            </Upload.Dragger>
            {fileUrl && (
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
                <Space>
                  <FileOutlined style={{ color: '#52c41a' }} />
                  <Typography.Text>{uploadedName || fileUrl.split('/').pop()}</Typography.Text>
                  <Typography.Link href={getImageUrl(fileUrl)} target="_blank" rel="noreferrer">
                    预览
                  </Typography.Link>
                </Space>
              </div>
            )}
          </Form.Item>
          <Space style={{ width: '100%' }}>
            <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
              <Select style={{ width: 150 }}>
                <Option value="strategies">交易策略</Option>
                <Option value="analysis">市场分析</Option>
                <Option value="guides">新手指南</Option>
                <Option value="references">快速参考</Option>
              </Select>
            </Form.Item>
            <Form.Item name="icon" label="图标">
              <Input style={{ width: 80 }} placeholder="📄" />
            </Form.Item>
            <Form.Item name="pages" label="页数">
              <InputNumber min={1} />
            </Form.Item>
          </Space>
          <Space>
            <Form.Item name="isVip" label="VIP专属" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="isPublished" label="发布状态" valuePropName="checked">
              <Switch checkedChildren="发布" unCheckedChildren="草稿" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </Space>
  );
};

export default Documents;
